import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { getSaveSlotDisplayProgramName, isSaveSlotEmpty } from '../../utils/saveSlotProjectId';
import ProgramManagementErrorDialog from '../Dialogs/ErrorDialogs/ProgramManagementErrorDialog';
import MainProgramIcon from '../Icons/Program/main-program.svg?react';

export interface SlotInfo {
  slotNumber?: number;
  programTitle?: string;
  isEmpty?: boolean;
  bpsProjectId?: string;
  bpsProjectLastModifiedUtcMs?: number;
  deviceFilename?: string;
}

interface SwerveSlotRowProps {
  slotNumber: number;
  slot: SlotInfo | null;
  disableActions?: boolean;
  destructiveActionsDisabled?: boolean;
  replaceProgramDisabled?: boolean;
  moveProgramDisabled?: boolean;
  onPreviewPython: () => void;
  onReplaceProgram: () => void;
  onMoveToSlot: () => void;
  onSaveAsNew: () => void;
  onBackupToDevice: () => void | Promise<void>;
  onDelete: () => void;
}

const SwerveSlotRow = ({
  slotNumber,
  slot,
  disableActions = false,
  destructiveActionsDisabled = false,
  replaceProgramDisabled = false,
  moveProgramDisabled = false,
  onPreviewPython,
  onReplaceProgram,
  onMoveToSlot,
  onSaveAsNew,
  onBackupToDevice,
  onDelete,
}: SwerveSlotRowProps) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [programManagementErrorOpen, setProgramManagementErrorOpen] = React.useState(false);
  const empty = isSaveSlotEmpty(slot);
  const actionsDisabled = disableActions || empty;
  const deleteDisabled = actionsDisabled || destructiveActionsDisabled;
  const destructiveMenuActionsDisabled = disableActions || destructiveActionsDisabled;
  const slotName = empty ? t('connectedDrawer.swerveSlot.emptySlot', 'Empty Slot') : getSaveSlotDisplayProgramName(slot?.programTitle);
  const slotLabel = t('slot.slotLabel', 'Slot {{slotNumber}}: {{slotName}}', { slotName, slotNumber });
  const destructiveActionsDisabledLabel = t(
    'connectedDrawer.userPrograms.actions-unverified',
    'Unavailable until drive unit slot metadata is verified'
  );
  const moreOptionsLabel = t('aria-label.more-options', 'more options');
  const deleteLabel = t('aria-label.delete', 'delete');

  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (actionsDisabled) return;
    setMenuAnchor(e.currentTarget);
  };
  const handleMenuClose = () => setMenuAnchor(null);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!deleteDisabled) onDelete();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1,
        px: 1,
        borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' },
        ...(empty && { color: 'text.disabled' }),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} flex={1} minWidth={0}>
        <Box
          component={MainProgramIcon}
          aria-hidden
          sx={{
            width: 22,
            height: 22,
            display: 'block',
            color: empty ? 'text.disabled' : 'text.primary',
            flexShrink: 0,
            opacity: empty ? 0.5 : 1,
          }}
        />
        <Typography
          variant="body2"
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            ...(empty && { color: 'text.disabled', fontStyle: 'italic' }),
          }}
        >
          {slotLabel}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0}>
        <Tooltip
          title={<Trans i18nKey="programState.unavailableWhileRuntimeBusy">Unavailable while the drive unit is busy</Trans>}
          disableHoverListener={!disableActions}
          disableFocusListener={!disableActions}
          disableTouchListener={!disableActions}
        >
          <span>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              disabled={actionsDisabled}
              aria-label={moreOptionsLabel}
              sx={{ color: actionsDisabled ? 'text.disabled' : undefined }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip
          title={
            disableActions ? (
              <Trans i18nKey="programState.unavailableWhileRuntimeBusy">Unavailable while the drive unit is busy</Trans>
            ) : destructiveActionsDisabled ? (
              destructiveActionsDisabledLabel
            ) : empty ? (
              ''
            ) : (
              <Trans i18nKey="driveUnitListItem.menu.delete">Delete from Drive Unit</Trans>
            )
          }
          disableHoverListener={!disableActions && empty}
          disableFocusListener={!disableActions && empty}
          disableTouchListener={!disableActions && empty}
        >
          <span>
            <IconButton
              size="small"
              onClick={handleDeleteClick}
              disabled={deleteDisabled}
              aria-label={deleteLabel}
              sx={{ color: deleteDisabled ? 'text.disabled' : undefined }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          disabled={actionsDisabled}
          onClick={() => {
            handleMenuClose();
            if (actionsDisabled) return;
            onPreviewPython();
          }}
        >
          <Trans i18nKey="driveUnitListItem.menu.preview-python">Preview Python</Trans>
        </MenuItem>
        <MenuItem
          disabled={replaceProgramDisabled || destructiveMenuActionsDisabled}
          onClick={() => {
            handleMenuClose();
            if (replaceProgramDisabled || destructiveMenuActionsDisabled) return;
            onReplaceProgram();
          }}
        >
          <Trans i18nKey="driveUnitListItem.menu.replace-program">Replace current program in project</Trans>
        </MenuItem>
        <MenuItem
          disabled={moveProgramDisabled || destructiveMenuActionsDisabled}
          onClick={() => {
            handleMenuClose();
            if (moveProgramDisabled || destructiveMenuActionsDisabled) return;
            onMoveToSlot();
          }}
        >
          <Trans i18nKey="driveUnitListItem.menu.move">Move to a different slot</Trans>
        </MenuItem>
        <MenuItem
          disabled={actionsDisabled}
          onClick={() => {
            handleMenuClose();
            if (actionsDisabled) return;
            onSaveAsNew();
          }}
        >
          <Trans i18nKey="driveUnitListItem.menu.save-as-new">Save as a new project</Trans>
        </MenuItem>
        <MenuItem
          disabled={actionsDisabled}
          onClick={() => {
            handleMenuClose();
            if (actionsDisabled) return;
            void Promise.resolve(onBackupToDevice()).catch(() => {
              setProgramManagementErrorOpen(true);
            });
          }}
        >
          <Trans i18nKey="driveUnitListItem.menu.backup">Backup to device</Trans>
        </MenuItem>
      </Menu>
      <ProgramManagementErrorDialog open={programManagementErrorOpen} onClose={() => setProgramManagementErrorOpen(false)} />
    </Box>
  );
};

export default SwerveSlotRow;
